<template>
  <textarea
    v-model="value"
    :class="[$style.textarea, { [$style['textarea--invalid']]: invalid }]"
    :rows="textRows"
    :disabled="disabled"
    :placeholder="placeholder"
  />
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'

export default defineComponent({
  name: 'DolphinTextarea',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    rows: {
      type: Number,
      default: 2,
    },
    autoGrow: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const value = computed({
      get: () => props.modelValue,
      set: (value: string) => emit('update:modelValue', value),
    })
    const textRows = computed((): number => {
      if (!props.autoGrow) {
        return props.rows
      }
      const inputRows = props.modelValue.split('\n').length
      return Math.max(inputRows + 1, props.rows)
    })

    return {
      value,
      textRows,
    }
  },
})
</script>

<style lang="scss" module>
$border: 1px solid $color-border-low-emphasis;

.textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 16px;
  border: $border;
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-base;
  color: $color-text-and-object-high-emphasis;
  font-size: 16px;
  font-weight: normal;
  line-height: 28px;
  resize: none;

  &--invalid {
    border-color: $color-caution-border;
    color: $color-caution-accent;
    background-color: $color-caution-surface;

    &:-webkit-autofill {
      color: $color-caution-accent;
      -webkit-text-fill-color: $color-caution-accent;
    }
  }

  &::placeholder {
    color: $color-text-and-object-low-emphasis;
  }

  &:disabled {
    border-color: $color-border-low-emphasis;
    background-color: $color-surface-secondary; // autofill hack
    color: $color-text-and-object-low-emphasis;

    &:-webkit-autofill {
      border-color: $color-border-low-emphasis;
      background-color: $color-surface-secondary; // autofill hack
      -webkit-text-fill-color: $color-text-and-object-low-emphasis;
    }
  }
}
</style>
